import React, {useEffect, useState, useRef} from "react";
import {Toggle, InputGroup, Input, toaster, InputNumber, Slider, Button} from "rsuite";
import moment, {Moment} from "moment";
import {ElectronicClockApi} from "../../../../../services/ElectronicClock.api";
import NotificationFactory from "../../../../../shared/Notifications";
import {SharedApi} from "../../../../../services/Shared.api";
import {extractTimezoneFromISO} from "../Dimmer/utils/Dimmer.utils";
import {formatTimezoneOffset} from "../../../../../shared/Time.utils";
import "./ElectronicClock.scss";
import ElectronicClockInfoSection from "./components/ElectronicClockInfoSection";
import {TfiSaveIcon, VscEditIcon} from "../../../../../models/Icons.model";
import DeviceEvents from "../../../../../components/shared/DeviceEvents/DeviceEvents";
import {
  DeviceTime,
  DeviceTimeResponse,
  DisplayDurations,
  ElectronicClockProps,
  Params,
  StoredMeteo
} from "./ElectronicClock.model";
import DateTimeEditModal from "../../../../modals/DateTimeEditModal/DateTimeEditModal";
import RebootConfirmationModal from "../Dimmer/components/RebootConfirmationModal";
import DevicesModal from "../../../../modals/DeviceModal/DeviceModal";
import {DeviceListItem, DevicesResponse} from "../../../../modals/modals.model";
import {ElectronicClockService, SetDeviceSourceModel} from "../../../../../api";

const ElectronicClockComponent = ({id, isAdmin, deviceId}: ElectronicClockProps) => {
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<Params>({});
  const [displayDurations, setDisplayDurations] = useState<DisplayDurations>({
    time: 0,
    date: 0,
    temperature: 0,
    storedTemperature: 0,
    storedPressure: 0,
    storedHumidity: 0
  });

  const [deviceData, setDeviceData] = useState<DeviceTimeResponse | null>(null);
  const [deviceTime, setDeviceTime] = useState<DeviceTime>(null);
  const [userTimezone, setUserTimezone] = useState("+3");
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const timeMomentRef = useRef<Moment>(moment());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [displayToggles, setDisplayToggles] = useState({
    time: false,
    date: false,
    temperature: false,
    storedTemperature: false,
    storedPressure: false,
    storedHumidity: false
  });
  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [isMeteoModalOpen, setIsMeteoModalOpen] = useState(false);
  const [meteoSourceDeviceId, setMeteoSourceDeviceId] = useState<string | null>(null);
  const [meteoSourceLabel, setMeteoSourceLabel] = useState("По координатам");

  useEffect(() => {
    if (id !== null && id !== undefined) {
      getElectronicClockParams().then();
      getTime().then();
    }
  }, [id]);

  useEffect(() => {
    setDisplayToggles({
      time: displayDurations.time > 0,
      date: displayDurations.date > 0,
      temperature: displayDurations.temperature > 0,
      storedTemperature: displayDurations.storedTemperature > 0,
      storedPressure: displayDurations.storedPressure > 0,
      storedHumidity: displayDurations.storedHumidity > 0
    });
  }, [displayDurations]);

  const getElectronicClockParams = async () => {
    setLoading(true);
    try {
      const response = await ElectronicClockService.getApiElectronicClockParams(id as string);
      const responseRecord = response as Record<string, unknown>;
      const durations = (responseRecord.displayDurations as Record<string, unknown> | undefined) ?? {};
      const storedMeteoRecord = (responseRecord.storedMeteo as Record<string, unknown> | undefined) ?? {};
      const paramsData: Params = {
        displayEnabled: Boolean(responseRecord.displayEnabled),
        displayBrightness: Number(responseRecord.displayBrightness ?? 0),
        sensorTemperature: Number(responseRecord.sensorTemperature ?? 0),
        temperature: Number(responseRecord.temperature ?? 0),
        pressure: Number(responseRecord.pressure ?? 0),
        humidity: Number(responseRecord.humidity ?? 0),
        storedMeteo: {
          temperature: Number(storedMeteoRecord.temperature ?? 0),
          pressure: Number(storedMeteoRecord.pressure ?? 0),
          humidity: Number(storedMeteoRecord.humidity ?? 0),
        } as StoredMeteo,
        displayDurations: {
          time: Number(durations.time ?? 0),
          date: Number(durations.date ?? 0),
          temperature: Number(durations.temperature ?? 0),
          storedTemperature: Number(durations.storedTemperature ?? 0),
          storedPressure: Number(durations.storedPressure ?? 0),
          storedHumidity: Number(durations.storedHumidity ?? 0),
        },
      };

      setParams(paramsData);
      setDisplayDurations({
        time: paramsData.displayDurations?.time || 0,
        date: paramsData.displayDurations?.date || 0,
        temperature: paramsData.displayDurations?.temperature || 0,
        storedTemperature: paramsData.displayDurations?.storedTemperature || paramsData.temperature || 0,
        storedPressure: paramsData.displayDurations?.storedPressure || paramsData.pressure || 0,
        storedHumidity: paramsData.displayDurations?.storedHumidity || paramsData.humidity || 0,
      });

      const meteoDevice = (responseRecord.meteoDevice as Record<string, unknown> | undefined) ?? {};
      const selectedDeviceId = (meteoDevice.deviceId as string | null | undefined) ?? null;
      setMeteoSourceDeviceId(selectedDeviceId);
      await resolveMeteoSourceLabel(selectedDeviceId);
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDurationChange =
    (field: keyof DisplayDurations) =>
      (value: string | number | null, _event: React.SyntheticEvent) => {
        const num = typeof value === "string" ? Number(value) : value ?? 0;
        if (Number.isNaN(num)) return;
        if (num >= 0 && num <= 99) {
          setDisplayDurations((prev) => ({
            ...prev,
            [field]: num
          }));
        }
      };

  const updateSerialState = async (enabled: boolean) => {
    setLoading(true);
    try {
      const result = await ElectronicClockApi.updateSerialState(id as string, enabled);

      if (result) {
        setShowSerialNumber(enabled);

        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisplay = async (checked: boolean) => {
    setLoading(true);
    try {
      const result = await ElectronicClockApi.updateState(id as string, checked);
      if (result) {
        getElectronicClockParams().then();
        toaster.push(
          NotificationFactory.success("Состояние дисплея обновлено"),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error("Не удалось обновить состояние дисплея"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка при обновлении состояния"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeBrightness = async (value: number | null) => {
    if (value === null) return;
    setLoading(true);
    try {
      const ok = await ElectronicClockApi.updateBrightness(id as string, value);
      if (ok) {
        getElectronicClockParams().then();
        toaster.push(
          NotificationFactory.success("Яркость обновлена"),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error("Не удалось обновить яркость"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка при обновлении яркости"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUtcDateTime = () => {
    return moment().utc().format("DD.MM.YYYY HH:mm:ss");
  };

  const getTimeMoment = (date: Moment | Date | string | null) => {
    if (!date) return moment();

    try {
      if (typeof date === "string") {
        return moment(date, "DD.MM.YYYY HH:mm:ss");
      }

      if (date instanceof Date) {
        return moment(date);
      }

      if (moment.isMoment(date)) {
        return date.clone();
      }

      return moment();
    } catch (error) {
      console.error("Error creating moment:", error);
      return moment();
    }
  };

  const updateTimeFromDeviceTime = () => {
    if (deviceTime) {
      const m = getTimeMoment(deviceTime);
      timeMomentRef.current = m;
      setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
    } else {
      timeMomentRef.current = moment();
      setCurrentTime("");
    }
  };

  useEffect(() => {
    setCurrentUtcDateTime(getCurrentUtcDateTime());
    const utcIntervalId = setInterval(() => {
      setCurrentUtcDateTime(getCurrentUtcDateTime());
    }, 1000);

    return () => clearInterval(utcIntervalId);
  }, []);

  useEffect(() => {
    updateTimeFromDeviceTime();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (timeMomentRef.current) {
        timeMomentRef.current.add(1, "seconds");
        setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [deviceTime]);

  const onSaveUTCTime = async (newDate?: Date) => {
    setLoading(true);
    try {
      const timeToSave = newDate ?? (deviceTime instanceof Date ? deviceTime : null);
      if (!timeToSave) {
        throw new Error("Не выбрано время для сохранения");
      }

      await SharedApi.updateUTCTime(id as string, timeToSave.toISOString() as unknown as string);
      toaster.push(
        NotificationFactory.success("Успешно обновлено"),
        {placement: "topEnd"}
      );
      await getTime();
    } catch (e) {
      console.error("Ошибка при сохранении времени:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const onSaveTimezone = async () => {
    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone);
      await SharedApi.updateTimezone(id as string, formattedTimezone);
      toaster.push(
        NotificationFactory.success("Успешно обновлено"),
        {placement: "topEnd"}
      );
      await getTime();
    } catch (e) {
      console.error("Ошибка при сохранении времени:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const getTime = async () => {
    setLoading(true);

    try {
      const data = (await SharedApi.getTime(id as string)) as DeviceTimeResponse;
      setDeviceData(data);

      if (data.time) {
        const timezone = extractTimezoneFromISO(data.time);
        setUserTimezone(timezone);

        const match = data.time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [, year, month, day, hours, minutes, seconds] = match;

          const date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
          );

          setDeviceTime(date);
          const m = getTimeMoment(date);
          timeMomentRef.current = m;
          setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
        } else {
          const date = new Date(data.time);
          setDeviceTime(date);
          const m = getTimeMoment(date);
          timeMomentRef.current = m;
          setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
        }
      } else {
        setDeviceTime(null);
        setCurrentTime("");
      }

    } catch (e) {
      console.error("Ошибка получения данных:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (value: string) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const updateDaylightSaving = async (enabled: boolean) => {
    setLoading(true);
    try {
      const result = await SharedApi.updateDaylightSaving(id as string, enabled);

      if (result) {
        setDeviceData((prev) => ({
          ...prev,
          isDaylightSavingEnabled: enabled
        }));
        toaster.push(
          NotificationFactory.success("Успешно обновлено"),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error("Ошибка"),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error("Ошибка при обновлении настроек:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDaylightSaving = async (enabled: boolean) => {
    await updateDaylightSaving(enabled);
  };

  const handleUpdateScreenDelay = async () => {
    setLoading(true);

    const payload = {
      time: +displayDurations.time,
      date: +displayDurations.date,
      temperatureFromSensor: +displayDurations.temperature,
      temperature: +displayDurations.storedTemperature,
      pressure: +displayDurations.storedPressure,
      humidity: +displayDurations.storedHumidity
    };

    try {
      await ElectronicClockApi.updateScreenDelay(id as string, payload);
      toaster.push(
        NotificationFactory.success("Успешно обновлено"),
        {placement: "topEnd"}
      );
      await getElectronicClockParams();
    } catch (e) {
      console.error("Ошибка при обновлении:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisplayField = (field: keyof DisplayDurations, checked: boolean) => {
    if (checked) {
      setDisplayDurations((prev) => ({
        ...prev,
        [field]: prev[field] > 0 ? prev[field] : 1
      }));
    } else {
      setDisplayDurations((prev) => ({
        ...prev,
        [field]: 0
      }));
    }
  };

  const formatMeteoSourceLabel = (device: DeviceListItem) => {
    if (device.kind === "coords") {
      return "По координатам";
    }

    const title = device.kind === "clock" ? "Часовой механизм" : "Блок датчиков";
    const model = device.model ? ` ${device.model}` : "";
    const address = device.address ? ` | ${device.address}` : "";

    return `${title}${model}${address}`;
  };

  const resolveMeteoSourceLabel = async (selectedDeviceId: string | null) => {
    if (!selectedDeviceId) {
      setMeteoSourceLabel("По координатам");
      return;
    }

    if (!deviceId) {
      setMeteoSourceLabel("Выбрано устройство");
      return;
    }

    try {
      const response = (await SharedApi.getDevices(String(deviceId))) as DevicesResponse;
      const {clocks = [], sensors = []} = response || {};
      const devices: DeviceListItem[] = [
        ...clocks.map((item) => ({...item, kind: "clock" as const})),
        ...sensors.map((item) => ({...item, kind: "sensor" as const})),
      ];
      const selectedDevice = devices.find((item) => String(item.id) === selectedDeviceId);

      setMeteoSourceLabel(
        selectedDevice ? formatMeteoSourceLabel(selectedDevice) : `Устройство ${selectedDeviceId}`
      );
    } catch (e) {
      console.error("Ошибка получения списка метео-устройств:", e);
      setMeteoSourceLabel(`Устройство ${selectedDeviceId}`);
    }
  };

  const handleSelectMeteoDevice = async (device: DeviceListItem) => {
    if (!id) return;

    setLoading(true);
    try {
      const selectedDeviceId = device.id === "coordinates" ? null : String(device.id);
      const payload: SetDeviceSourceModel = {deviceId: selectedDeviceId};
      await ElectronicClockService.postApiElectronicClockMeteoSource(String(id), payload);

      setMeteoSourceDeviceId(selectedDeviceId);
      setMeteoSourceLabel(formatMeteoSourceLabel(device));
      await getElectronicClockParams();
      toaster.push(
        NotificationFactory.success(),
        {placement: "topEnd"}
      );
    } catch (e) {
      console.error("Ошибка обновления источника метео-данных:", e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRebootModal = () => {
    setShowRebootModal(true);
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleConfirmReboot = async () => {
    setShowRebootModal(false);
    setLoading(true);
    try {
      const result = await SharedApi.rebootDevice(String(id));

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error(),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
      console.error("Ошибка при перезапуске устройства:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fieldset-wrapper">
        <div className="legend-title main-legend-title">Электронные часы</div>
        <div className="row p-2 mt-3 pb-0">
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12">
            <Toggle
              className="me-2"
              size="md"
              checked={params?.displayEnabled}
              loading={loading}
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              onChange={handleToggleDisplay}
            />
            Включить / Выключить индикацию
          </div>
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12">
            <Toggle
              className="me-2"
              size="md"
              checked={showSerialNumber}
              loading={loading}
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              onChange={(e) => updateSerialState(e)}
            />
            Вывести серийный номер
          </div>
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12 electronic-clock__meteo-source">
            <span className="electronic-clock__meteo-source-label">Метеоданные:</span>
            <Button
              size="xs"
              appearance="subtle"
              disabled={loading || !deviceId}
              title={meteoSourceDeviceId ? `ID: ${meteoSourceDeviceId}` : "По координатам"}
              onClick={() => setIsMeteoModalOpen(true)}
            >
              {meteoSourceLabel}
            </Button>
          </div>
        </div>
        <div className="row p-2 pb-0">
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12">
            <div className="mb-2">
              Яркость: {params.displayBrightness}
            </div>
            <Slider
              min={0}
              max={100}
              step={1}
              value={params.displayBrightness}
              onChange={handleChangeBrightness}
              disabled={loading}
            />
          </div>
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12 mt-2">
            <Toggle
              size="md"
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={deviceData?.isDaylightSavingEnabled || false}
              onChange={handleToggleDaylightSaving}
              disabled={loading}
              loading={loading}
            >
              Авт. переход зима / лето
            </Toggle>
          </div>
        </div>
        <div className="row p-2 mt-2">
          <div className="col-md-4">
            <div className="section-text time-wrapper">
              <div style={{width: "100%"}}>
                <InputGroup style={{marginBottom: "5px", width: "100%"}}>
                  <InputGroup.Addon>Время и дата</InputGroup.Addon>
                  <Input
                    className="bg-green"
                    readOnly
                    value={currentTime}
                  />
                  <InputGroup.Addon
                    onClick={() => setIsTimeModalOpen(true)}
                    style={{cursor: "pointer"}}
                    title="Редактировать"
                  >
                    <VscEditIcon/>
                  </InputGroup.Addon>
                </InputGroup>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <InputGroup className="mb-1">
              <InputGroup.Addon>Час. пояс</InputGroup.Addon>
              <Input
                value={userTimezone}
                onChange={handleTimezoneChange}
                style={{textAlign: "center"}}
              />
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{cursor: "pointer"}}
                  onClick={onSaveTimezone}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-4">
            <InputGroup style={{marginBottom: "5px"}}>
              <InputGroup.Addon>Время и дата UTC</InputGroup.Addon>
              <Input
                className="bg-yellow"
                readOnly={true}
                value={currentUtcDateTime}
              />
            </InputGroup>
          </div>
        </div>
        <div className="row p-2">
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.time}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("time", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.time}
                    max={99}
                    onChange={handleDurationChange("time")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">Время -- / --</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.date}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("date", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.date}
                    max={99}
                    onChange={handleDurationChange("date")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">Дата -- / --</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.temperature}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("temperature", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.temperature}
                    max={99}
                    onChange={handleDurationChange("temperature")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">Темп. С° : {params?.sensorTemperature}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="row p-2">
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.storedTemperature}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("storedTemperature", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.storedTemperature}
                    max={99}
                    onChange={handleDurationChange("storedTemperature")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">
                  Температура : {params?.storedMeteo?.temperature ?? "--"}
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.storedPressure}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("storedPressure", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.storedPressure}
                    max={99}
                    onChange={handleDurationChange("storedPressure")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">
                  Давление : {params?.storedMeteo?.pressure ?? "--"}
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Toggle
                className="me-2"
                size="md"
                checked={displayToggles.storedHumidity}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={(checked) => handleToggleDisplayField("storedHumidity", checked)}
              />
              <div className="d-flex align-items-center mb-1">
                <InputGroup style={{width: 100}}>
                  <InputNumber
                    value={displayDurations.storedHumidity}
                    max={99}
                    onChange={handleDurationChange("storedHumidity")}
                    disabled={loading}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon style={{cursor: "pointer"}} onClick={handleUpdateScreenDelay}/>
                  </InputGroup.Addon>
                </InputGroup>
                <p className="info-title ms-3">
                  Влажность : {params?.storedMeteo?.humidity ?? "--"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row p-2 align-items-center pb-0">
        <div className="row p-2 align-items-center">
          <div className="col-xxl-6 col-xl-12 col-lg-12 col-md-12">
            <ElectronicClockInfoSection id={id} isAdmin={isAdmin}/>
            <div className="text-center mt-1">
              <Button
                color="red"
                appearance="primary"
                type="button"
                onClick={handleOpenRebootModal}
                loading={loading}
                disabled={loading}
              >
                Перезапустить устройство
              </Button>
            </div>
          </div>
        </div>
        <div className="row px-2 pb-2">
          <div className="col-md-12">
            <DeviceEvents deviceId={id}/>
          </div>
        </div>
      </div>
      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime instanceof Date ? deviceTime : null}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment) => {
          const newDate = newMoment.toDate();

          setDeviceTime(newDate);
          timeMomentRef.current = newMoment.clone();
          setCurrentTime(newMoment.format("DD.MM.YYYY HH:mm:ss"));

          await onSaveUTCTime(newDate);
          setIsTimeModalOpen(false);
        }}
      />
      <RebootConfirmationModal
        show={showRebootModal}
        onHide={handleCloseRebootModal}
        onConfirm={handleConfirmReboot}
      />
      <DevicesModal
        open={isMeteoModalOpen}
        deviceId={deviceId}
        onClose={() => setIsMeteoModalOpen(false)}
        onSelectDevice={handleSelectMeteoDevice}
      />
    </>
  );
};

export default ElectronicClockComponent;
